import React, { useCallback, useEffect, useState } from 'react';
import { useCurrentUser } from '@/core/hooks/useCurrentUser';
import { TripRoute } from '../MapView/MapNavigation/MapNavigation.types';
import { ConfirmationRouteModal } from '../components/MapDemoControls/components/ConfirmationRouteModal';
import { isPocDemoOrgId } from '../components/MapDemoControls/isNavigationDemoOrgId';

interface DemoFlowContextData {
  isDemoOrgId: boolean;
  isProceedEvent: boolean;
  setProceedEvent(isProceedEvent: DemoFlowContextData['isProceedEvent']): void;
  isActiveProcess: boolean | undefined;
  isCleanedDemo: boolean;
  isConfirmed: boolean;
  showConfirmationRoute: TripRoute[] | null;
  setIsActive(isActiveProcess: DemoFlowContextData['isActiveProcess']): void;
  showConfirmationModal(route: DemoFlowContextData['showConfirmationRoute']): void;
}

const DemoFlowContext = React.createContext<DemoFlowContextData>({
  isDemoOrgId: false,
  isProceedEvent: false,
  setProceedEvent: () => null,
  isActiveProcess: false,
  isCleanedDemo: false,
  isConfirmed: false,
  showConfirmationRoute: null,
  setIsActive: () => null,
  showConfirmationModal: () => null,
});

export const useDemoFlowContext = () => React.useContext(DemoFlowContext);

export const DemoFlowContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isActiveProcess, setIsActive] = useState<DemoFlowContextData['isActiveProcess']>(false);
  const [isProceedEvent, setProceedEvent] = useState<DemoFlowContextData['isProceedEvent']>(false);
  const [showConfirmationRoute, setShowConfirmationRoute] =
    useState<DemoFlowContextData['showConfirmationRoute']>(null);
  const [isConfirmed, setIsConfirmed] = useState<DemoFlowContextData['isConfirmed']>(false);
  const { orgId } = useCurrentUser();
  const isDemoOrgId = isPocDemoOrgId(orgId);

  useEffect(() => {
    if (isActiveProcess === undefined) {
      setProceedEvent(false);
      setIsConfirmed(false);
    }
  }, [isActiveProcess]);

  const onConfirmRoute = useCallback(() => {
    setIsActive(true);
    setShowConfirmationRoute(null);
    setIsConfirmed(true);
  }, []);

  const showConfirmationModal = useCallback((routes: TripRoute[]) => {
    setShowConfirmationRoute(routes);
  }, []);

  return (
    <DemoFlowContext.Provider
      value={{
        isDemoOrgId,
        isProceedEvent,
        setProceedEvent,
        isCleanedDemo: isActiveProcess === undefined,
        isActiveProcess,
        isConfirmed,
        showConfirmationRoute,
        setIsActive,
        showConfirmationModal,
      }}
    >
      {children}
      {showConfirmationRoute && (
        <ConfirmationRouteModal
          routes={showConfirmationRoute}
          onConfirm={onConfirmRoute}
          onClose={() => setShowConfirmationRoute(null)}
        />
      )}
    </DemoFlowContext.Provider>
  );
};
